/**
 * Learning progress + Football IQ persistence.
 *
 * Domain rules live in `utils/iqRewards.js`; this module only decides what
 * to read and write. Reward maths is never done inline in a component.
 */

import { getBackend } from "../backend";
import {
  LEARNING_SOURCES,
  awardLearningXP,
  scoreQuizAnswer,
} from "../../utils/iqRewards";

/** @returns {Promise<Object.<string, object>>} conceptId → record */
export async function loadProgress(uid) {
  if (!uid) return {};
  const records = await getBackend().listLearningRecords(uid);
  return records.reduce((acc, record) => {
    if (record?.conceptId) acc[record.conceptId] = record;
    return acc;
  }, {});
}

/**
 * Record that a user learned a concept and pay out the XP, but only once.
 *
 * @returns {Promise<{progress: object, xpAwarded: number, alreadyLearned: boolean}>}
 */
export async function recordConceptLearned(
  uid,
  progress,
  conceptId,
  source = LEARNING_SOURCES.EXPLANATION
) {
  const outcome = awardLearningXP(progress, conceptId, { source });

  if (!uid || !outcome.record) return outcome;

  const backend = getBackend();
  await backend.saveLearningRecord(uid, outcome.record);

  if (outcome.xpAwarded > 0) {
    await backend.updateUserProfile(uid, {
      footballIQ: { __increment: outcome.xpAwarded },
      conceptsLearned: { __increment: 1 },
      lastActiveAt: Date.now(),
    });
  }

  return outcome;
}

/**
 * Grade a quiz answer, pay the XP, and mark the underlying concept learned.
 *
 * @returns {Promise<{correct: boolean, xpAwarded: number, correctOptionId: string|null}>}
 */
export async function recordQuizAnswer(uid, progress, question, answerId) {
  const graded = scoreQuizAnswer(question, answerId);

  if (!uid) return graded;

  const backend = getBackend();
  let totalXp = graded.xpAwarded;
  let conceptsDelta = 0;
  let nextProgress = progress;

  if (graded.correct && question?.conceptId) {
    const learned = awardLearningXP(progress, question.conceptId, {
      source: LEARNING_SOURCES.QUIZ,
    });
    nextProgress = learned.progress;
    totalXp += learned.xpAwarded;
    if (!learned.alreadyLearned) conceptsDelta = 1;
    if (learned.record) await backend.saveLearningRecord(uid, learned.record);
  }

  if (totalXp > 0 || conceptsDelta > 0) {
    await backend.updateUserProfile(uid, {
      footballIQ: { __increment: totalXp },
      conceptsLearned: { __increment: conceptsDelta },
      lastActiveAt: Date.now(),
    });
  }

  return { ...graded, xpAwarded: totalXp, progress: nextProgress };
}

/** Credit XP that was earned outside the learning system (games, predictions). */
export async function awardXP(uid, amount, extraPatch = {}) {
  const xp = Math.max(0, Math.floor(Number(amount) || 0));
  if (!uid || xp === 0) return 0;

  await getBackend().updateUserProfile(uid, {
    footballIQ: { __increment: xp },
    lastActiveAt: Date.now(),
    ...extraPatch,
  });

  return xp;
}
